import { useCallback, useEffect, useState } from 'react';

// Hard navigations to legacy server-rendered pages (`/reopen`, `/bidding`,
// `/payment`) leave the browser white between click and the next paint —
// `<a href>` alone gives no feedback that the click landed. This hook
// pre-paints `BusyOverlay` for that window so the customer sees an
// "Opening your order" card the moment the row is clicked, mirroring the
// auth flow's "Signing you in" overlay.
//
// Usage in a page component:
//
//     const { navigatingMessage, navigate } = useExternalNavigation();
//     // ...
//     <a href="/reopen?orderId=1" onClick={(e) => {
//         e.preventDefault();
//         navigate('/reopen?orderId=1');
//     }} />
//     {navigatingMessage && <BusyOverlay message={navigatingMessage} />}
//
// Modifier-clicks (cmd/ctrl/middle/shift) should be intercepted *before*
// calling `navigate` — they're "open in new tab" gestures and the host
// page stays alive, so an overlay would be wrong.
// Order matters — first match wins. More-specific paths must come before
// the bare-prefix fallbacks (`/account/balance` before `/account`).
const MESSAGE_RULES = [
    [/^\/reopen\b/, 'Reopening your order'],
    [/^\/bidding\b/, 'Opening your order'],
    [/^\/payment\b/, 'Opening checkout'],
    [/^\/order\b/, 'Starting a new order'],
    [/^\/account\/balance\b/, 'Opening your balance'],
    [/^\/account\/profile\b/, 'Opening your profile'],
    [/^\/account\/support\b/, 'Opening support'],
    [/^\/account\/refer-a-friend\b/, 'Opening your referrals'],
    [/^\/account\/loyalty-program\b/, 'Opening loyalty program'],
    [/^\/account\/orders\/\d+/, 'Opening your order'],
    [/^\/account\b/, 'Opening your orders'],
];

function pickMessage(href) {
    for (const [pattern, msg] of MESSAGE_RULES) {
        if (pattern.test(href)) return msg;
    }
    return 'Opening your order';
}

export default function useExternalNavigation() {
    const [navigatingMessage, setNavigatingMessage] = useState(null);

    // Clear the overlay when the page is restored from the bfcache (Back /
    // Forward). The navigation sets the overlay then leaves; the browser
    // snapshots the frozen DOM *with the overlay up*, so hitting Back would
    // otherwise restore an eternal "Opening your order" preloader. `pageshow`
    // with `persisted` fires only on a bfcache restore — reset there.
    useEffect(() => {
        const onPageShow = (e) => {
            if (e.persisted) setNavigatingMessage(null);
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    const navigate = useCallback((href) => {
        if (!href) return;
        setNavigatingMessage(pickMessage(href));
        // rAF lets the overlay paint at least once before the browser
        // freezes the document for the navigation request. Without the
        // delay Chrome can swallow the `<dialog>` paint into the network
        // gap, which defeats the whole point of the hook.
        requestAnimationFrame(() => {
            window.location.href = href;
        });
    }, []);

    return { navigatingMessage, navigate };
}
